// Components
import * as Select from '@radix-ui/react-select'
import React, { type ReactNode, useState } from 'react'
// Icons
import { PiCaretDown, PiCheck } from 'react-icons/pi'
// Lib
import { twMerge } from 'tailwind-merge'
import { tv, type VariantProps } from 'tailwind-variants'

const selectVariants = tv({
  base: 'absolute text-base text-textDisabled duration-300 group-enabled:group-hover:text-text-primary  group-enabled:group-data-[active=true]:text-text-primary group-enabled:group-data-[error=true]:group-data-[active=true]:text-error group-disabled:text-borderColor/30',
  variants: {
    variant: {
      form: 'group-data-[active=true]:mt-[-78px] group-data-[state=true]:ml-[-0.75rem] group-enabled:group-data-[active=true]:text-text-primary group-data-[active=true]:ml-[-0.75rem]',
      filter:
        'group-data-[active=true]:mt-[-46px] group-data-[active=true]:ml-[-4px] group-data-[active=true]:text-xs block px-1 bg-white group-enabled:group-data-[active=true]:text-text-primary',
    },
  },
  defaultVariants: {
    variant: 'filter',
  },
})

type RootProps = Omit<Select.SelectProps, 'onOpenChange' | 'open'> &
  VariantProps<typeof selectVariants> & {
    children: ReactNode
    label: string
    className?: string
    placeholder?: string | null
    errorMessage?: string | null
  }

export function Root({
  children,
  variant,
  label,
  placeholder = '',
  errorMessage,
  className,
  ...props
}: RootProps): JSX.Element {
  const [isOpen, setIsOpen] = useState(props.defaultOpen ?? false)

  const hasError = !!errorMessage
  const isDisabled = props.disabled
  const hasSomeSelected = !!props.value || !!props.defaultValue
  const shouldDisplayPlaceholder = !hasSomeSelected && isOpen

  return (
    <div className={twMerge('w-full space-y-1.5', className)}>
      <div className="flex flex-col w-full relative">
        <Select.Root {...props} onOpenChange={setIsOpen}>
          <Select.Trigger
            data-error={hasError}
            data-disabled={isDisabled}
            data-active={hasSomeSelected || isOpen}
            disabled={isDisabled}
            className="
              border h-10 border-[#E1E1E1] z-10 group flex items-center justify-between p-3 rounded-[0.375rem] transition-all
              enabled:data-[error=true]:border-error
              enabled:data-[error=true]:data-[state=open]:border-error
              enabled:hover:border-text-primary enabled:hover:cursor-pointer
              disabled:border-borderColor/30
              enabled:data-[state=open]:border-text-primary
              enabled:focus-within:border-text-primary enabled:focus:outline-none
              data-[disabled=true]:text-borderColor/30"
          >
            <span className={selectVariants({ variant })}>{label}</span>

            <Select.Value
              data-disabled={isDisabled}
              placeholder={shouldDisplayPlaceholder ? placeholder : ''}
              className="text-text-primary text-base data-[disabled=true]:text-borderColor/30 placeholder:text-text-disabled"
            />

            <Select.Icon className="ml-auto">
              <PiCaretDown
                color={
                  hasError
                    ? '#FF3A28'
                    : isDisabled
                      ? 'rgba(140,140,140, 0.3)'
                      : '#24262B'
                }
                className="group-hover:stroke-text-primary duration-300 group-data-[state=open]:rotate-180"
              />
            </Select.Icon>
          </Select.Trigger>

          <Select.Portal>
            <Select.Content
              side="bottom"
              position="popper"
              className="
                w-[--radix-select-trigger-width] bg-white max-h-[15rem] p-2 border border-text-primary z-[9999999] overflow-auto rounded-[0.375rem]
                scrollbar-thin scrollbar-thumb-primary"
              sideOffset={8}
            >
              <Select.Viewport>{children}</Select.Viewport>
            </Select.Content>
          </Select.Portal>
        </Select.Root>
      </div>

      {hasError && (
        <span className="block text-error text-sm">{errorMessage}</span>
      )}
    </div>
  )
}

type ItemProps = Select.SelectItemProps & {
  text: string
  Prefix?: JSX.Element
}

/**
 *
 * If you want to pass a prefix to the item, you must pass the prefix as a prop
 * @example
 * <Select.Item prefix={<PiCheck />} {..otherProps} />
 */
export function Item({ text, Prefix, ...props }: ItemProps): JSX.Element {
  return (
    <Select.Item
      className="outline-none text-text-primary duration-300 hover:border-none hover:outline-none p-2 pr-4 cursor-pointer hover:bg-zinc-50 w-full flex items-center justify-between"
      {...props}
    >
      {Prefix ? (
        <div className="flex-1 gap-4 flex items-center justify-start">
          {Prefix}
          <Select.ItemText className="ml-2 text-text-primary">
            {text}
          </Select.ItemText>
        </div>
      ) : (
        <Select.ItemText className="text-text-primary">{text}</Select.ItemText>
      )}

      <Select.ItemIndicator>
        <PiCheck />
      </Select.ItemIndicator>
    </Select.Item>
  )
}
